import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import { CharityLogo, Text, Box, Button, ContentBox, useAlert, NotificationDialog, FundLogo } from '..'
import { mutations, queries } from '../../graphql'
import { useRecordContext } from '../../management/management-components'

export function GenFundLogoUpload({ onLogoUpdate, onLogoLanguageConfirm }) {
  const { fundId } = useRecordContext()
  const fileInputRef = useRef(null)
  const [logoFile, setLogoFile] = useState()
  const [newLogo, setNewLogo] = useState(null)
  const [newLogoBinary, setNewLogoBinary] = useState(null)
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const alertWait = 3000

  useEffect(() => {
    if (logoFile) {
      onLogoLanguageConfirm(true)
    }
  }, [logoFile, onLogoLanguageConfirm])

  const { data: { getLogo: fund } = {} } = useQuery(queries.funds.getLogo, {
    variables: { fundId },
  })
  const myLogo = fund?.logo

  const [updateMyGenerosityFundLogo] = useMutation(mutations.funds.updateMyGenerosityFundLogo, {
    refetchQueries: ['getLogo'],
    onCompleted: () => {
      if (onLogoUpdate) {
        onLogoUpdate()
      }
      setAlert({
        message: 'Logo successfully updated!',
        severity: 'success',
      })
    },
  })

  const handleButtonClick = () => {
    fileInputRef.current.click()
  }

  const uploadNewLogo = (e) => {
    const file = e.target.files[0]
    setLogoFile(file)
    setNewLogo(URL.createObjectURL(file))

    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const base64Data = btoa(event.target.result)
        setNewLogoBinary(base64Data)
      }
      reader.readAsBinaryString(file)
    }
  }

  const resetLogoState = () => {
    setLogoFile(null)
    setNewLogo(null)
    setNewLogoBinary(null)
    if (document.getElementById('newLogoInput')) {
      document.getElementById('newLogoInput').value = null
    }
  }

  const handleSubmitLogo = async () => {
    if (newLogo) {
      try {
        await updateMyGenerosityFundLogo({
          variables: {
            data: {
              fundId,
              logo: newLogoBinary,
            },
          },
        })
        resetLogoState()
      } catch (error) {
        console.error('Error updating logo:', error)
        setAlert({
          message: 'Failed to update logo',
          severity: 'error',
          error: error.message,
          timeout: alertWait,
        })
      }
    }
  }

  const handleDeleteLogo = async () => {
    try {
      await updateMyGenerosityFundLogo({
        variables: {
          data: {
            fundId,
            logo: null,
          },
        },
      })
      setIsDeleteDialogOpen(false)
      resetLogoState()
      setAlert({
        message: 'Logo successfully deleted',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error deleting logo:', error)
      setAlert({
        message: 'Failed to delete logo. Please try again.',
        severity: 'error',
        error: error.message,
        timeout: alertWait,
      })
    }
  }

  const renderGenerosityFundLogo = () => {
    if (logoFile) {
      return <FundLogo temporarySrc={newLogo} />
    }

    if (myLogo) {
      return <FundLogo logo={myLogo || null} />
    }
    return <FundLogo />
  }

  return (
    <>
      <ContentBox
        border
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyItems: 'center',
          width: '100%',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            minWidth: '250px',
            maxWidth: '400px',
          }}
        >
          <div style={{ height: '140px', border: '1px solid lightGray', borderRadius: '10px' }}>
            {renderGenerosityFundLogo()}
          </div>

          <div>
            <Text.H7 style={{ margin: '10px 10px' }}>Upload New Logo Image (jpg/png):</Text.H7>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
              <input
                ref={fileInputRef}
                id="newLogoInput"
                type="file"
                accept=".jpg, .jpeg, .png"
                onChange={uploadNewLogo}
                style={{ display: 'none' }}
              />

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button type="button" onClick={handleButtonClick} style={{ width: 'fit-content', margin: '10px 10px' }}>
                  Choose File
                </Button>
              </div>
              {logoFile && <Text.AltBody sx={{ wordBreak: 'break-word' }}>Selected: {logoFile.name}</Text.AltBody>}
            </div>
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {logoFile && (
                <>
                  <Button type="button" onClick={handleSubmitLogo} style={{ margin: '10px 10px' }}>
                    Save Logo
                  </Button>
                  <Button type="button" onClick={resetLogoState} style={{ margin: '10px 10px' }}>
                    Cancel
                  </Button>
                </>
              )}
              {!newLogo && (myLogo?.logo || myLogo?.logoPath) && (
                <Button type="button" onClick={() => setIsDeleteDialogOpen(true)} style={{ margin: '10px 10px' }}>
                  Delete
                </Button>
              )}
            </div>
            <Alert {...alertProps} />
          </div>
        </div>
      </ContentBox>

      <NotificationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        type="alert"
        title="Delete Logo"
        message="Are you sure you want to delete the current logo? This action cannot be undone."
        actions={[
          { label: 'Cancel', onClick: () => setIsDeleteDialogOpen(false) },
          { label: 'Delete', onClick: handleDeleteLogo },
        ]}
      />
    </>
  )
}

export const CharityLogoUpload = forwardRef(
  (
    { charityId, onLogoUpdate, onLogoError, onLogoSelected, onLogoCanceled, onLogoLanguageConfirm, extraButtons },
    ref
  ) => {
    const fileInputRef = useRef(null)
    const [logoFile, setLogoFile] = useState()
    const [newLogo, setNewLogo] = useState(null)
    const [newLogoBinary, setNewLogoBinary] = useState(null)
    const [{ Alert, alertProps }, { setAlert }] = useAlert()
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const alertWait = 3000

    useEffect(() => {
      if (logoFile) {
        onLogoLanguageConfirm(true)
        // Notify parent that a logo has been selected but not saved yet
        if (onLogoSelected) {
          onLogoSelected()
        }
      }
    }, [logoFile, onLogoLanguageConfirm, onLogoSelected])

    // Cleanup object URL on unmount to prevent memory leak
    useEffect(
      () => () => {
        if (newLogo) {
          URL.revokeObjectURL(newLogo)
        }
      },
      [newLogo]
    )

    const { data: { getMyCharityLogo: myLogo } = {} } = useQuery(queries.charities.getMyCharityLogo, {
      variables: { id: charityId },
    })

    const [updateMyCharityLogo] = useMutation(mutations.charities.updateMyCharityLogo, {
      refetchQueries: ['getMyCharityLogo'],
      onCompleted: () => {
        if (onLogoUpdate) {
          onLogoUpdate()
        }
        setAlert({
          message: 'Logo successfully updated!',
          severity: 'success',
        })
      },
      onError: (error) => {
        console.error('Error updating logo:', error)
        setAlert({
          message: 'Failed to update logo',
          severity: 'error',
          error: error.message,
        })
        if (onLogoError) {
          onLogoError()
        }
      },
    })

    const handleButtonClick = () => {
      fileInputRef.current.click()
    }

    const uploadNewLogo = (e) => {
      const file = e.target.files[0]

      // User cancelled file picker
      if (!file) return

      // Revoke old object URL to prevent memory leak
      if (newLogo) {
        URL.revokeObjectURL(newLogo)
      }

      setLogoFile(file)
      const objectURL = URL.createObjectURL(file)
      setNewLogo(objectURL)

      const reader = new FileReader()
      reader.onload = (event) => {
        const base64Data = btoa(event.target.result)
        setNewLogoBinary(base64Data)
      }
      reader.readAsBinaryString(file)
    }

    const resetLogoState = () => {
      // Revoke object URL to prevent memory leak
      if (newLogo) {
        URL.revokeObjectURL(newLogo)
      }

      setLogoFile(null)
      setNewLogo(null)
      setNewLogoBinary(null)
      if (document.getElementById('newLogoInput')) {
        document.getElementById('newLogoInput').value = null
      }
    }

    const handleCancelLogo = () => {
      resetLogoState()
      // Notify parent that logo upload was canceled
      if (onLogoCanceled) {
        onLogoCanceled()
      }
    }

    const handleSubmitLogo = async () => {
      if (newLogo) {
        await updateMyCharityLogo({
          variables: {
            data: {
              charityId,
              logoPath: null,
              imagePath: null,
              logo: newLogoBinary,
            },
          },
        })
        resetLogoState()
      }
    }

    const handleDeleteLogo = async () => {
      try {
        await updateMyCharityLogo({
          variables: {
            data: {
              charityId,
              imagePath: null,
              logoPath: null,
              logo: null,
            },
          },
        })
        setIsDeleteDialogOpen(false)
        resetLogoState()
        setAlert({
          message: 'Logo successfully deleted',
          severity: 'success',
        })
      } catch (error) {
        console.error('Error deleting logo:', error)
        setAlert({
          message: 'Failed to delete logo. Please try again.',
          severity: 'error',
          error: error.message,
          timeout: alertWait,
        })
      }
    }

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      saveLogo: () => {
        handleSubmitLogo()
      },
      cancelLogo: () => {
        handleCancelLogo()
      },
    }))

    const renderCharityLogo = () => {
      if (logoFile) {
        return <CharityLogo charityId={charityId} temporarySrc={newLogo} />
      }

      if (myLogo) {
        return (
          <CharityLogo
            charityId={charityId}
            temporarySrc={myLogo?.logo ? `data:image/png;base64,${myLogo.logo}` : null}
            link={myLogo?.logoPath}
          />
        )
      }
      return <CharityLogo charityId={charityId} />
    }

    return (
      <>
        <ContentBox
          border
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyItems: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              minWidth: '250px',
              maxWidth: '400px',
            }}
          >
            <div style={{ height: '140px', border: '1px solid lightGray', borderRadius: '10px' }}>
              {renderCharityLogo()}
            </div>

            <div>
              <Text.H7 style={{ margin: '10px 10px' }}>Upload New Logo Image (jpg/png):</Text.H7>
              <input
                ref={fileInputRef}
                id="newLogoInput"
                type="file"
                accept=".jpg, .jpeg, .png"
                onChange={uploadNewLogo}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {extraButtons}
                <Button type="button" onClick={handleButtonClick} style={{ width: 'fit-content', margin: '10px 0' }}>
                  Choose File
                </Button>
                {logoFile && (
                  <>
                    <Button type="button" onClick={handleSubmitLogo} style={{ margin: '10px 0' }}>
                      Save Logo
                    </Button>
                    <Button type="button" onClick={handleCancelLogo} style={{ margin: '10px 0' }}>
                      Cancel
                    </Button>
                  </>
                )}
                {!newLogo && (myLogo?.logo || myLogo?.logoPath) && (
                  <Button type="button" onClick={() => setIsDeleteDialogOpen(true)} style={{ margin: '10px 0' }}>
                    Delete
                  </Button>
                )}
              </div>
              {logoFile && <Text.AltBody sx={{ wordBreak: 'break-word' }}>Selected: {logoFile.name}</Text.AltBody>}
              <Alert {...alertProps} />
            </div>
          </div>
        </ContentBox>

        <NotificationDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          type="alert"
          title="Delete Logo"
          message="Are you sure you want to delete the current logo? This action cannot be undone."
          actions={[
            { label: 'Cancel', onClick: () => setIsDeleteDialogOpen(false) },
            { label: 'Delete', onClick: handleDeleteLogo },
          ]}
        />
      </>
    )
  }
)
